import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react'
import { Loader2, LucideIcon } from 'lucide-react'

/**
 * LE BOUTON UNIQUE (LOT-H3).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER EXISTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * L'audit visuel du 2026-08 a compté **~14 traitements de bouton** sur 33 écrans :
 * bleu plein, dégradé bleu→violet, dégradé bleu→cyan, violet plein, cyan plein,
 * vert plein, vert contour, ambre plein, rouge plein, fantôme gris, contour bleu,
 * lien texte nu, icône nue, icône dans un carré fantôme. « Actualiser » — la même
 * action — existait en **quatre couleurs** selon la page.
 *
 * La cause n'est pas le goût, c'est l'absence de source unique : chaque page a
 * réécrit son `className`. Ce composant est cette source.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LA VARIANTE DIT CE QUE FAIT L'ACTION, PAS À QUEL POINT ON VEUT LA VOIR
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Contrat : `docs/design/DESIGN-SYSTEM.md` §5.
 *
 * | Variante          | Emploi                                                    |
 * |-------------------|-----------------------------------------------------------|
 * | `primary`         | L'action principale de l'écran. **Une seule par zone.**   |
 * | `secondary`       | Actions courantes non principales (export, import, filtre) |
 * | `ghost`           | Annuler, fermer, « Actualiser », actions de service       |
 * | `destructive`     | Supprimer, retirer, révoquer — **contour**, partout        |
 * | `destructiveSolid`| Rouge PLEIN — **confirmation finale en modale UNIQUEMENT** |
 *
 * ⚠️ `destructiveSolid` n'est pas « un destructif plus visible ». Un bouton rouge
 * plein est le point le plus lumineux d'un écran sombre : il attire le clic qu'il
 * devrait décourager (« Tout supprimer » était l'élément le plus visible de
 * `/contacts`). Il n'est légitime que comme DERNIÈRE étape d'une intention déjà
 * exprimée, c'est-à-dire dans la modale de confirmation — et nulle part ailleurs.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LES DEUX MÉTRIQUES NE SE CONFONDENT PAS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `h-control` (42 px) est la hauteur **dessinée**. `min-h-touch` (44 px) est la
 * cible **tactile** iPad. En taille `md` le bouton mesure 42 px de haut et porte
 * `min-h-touch` : le padding vertical l'amène à 44 px de zone cliquable sans le
 * faire grossir. En taille `sm` (32 px dessinés) la cible est rétablie par un
 * **pseudo-élément** de 44 px centré, qui déborde sans occuper de place dans le
 * flux — grossir le bouton dégraderait la densité écran par écran (§3).
 */

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'destructive'
  | 'destructiveSolid'

export type ButtonSize = 'md' | 'sm'

/**
 * Socle commun. `whitespace-nowrap` est une RÈGLE, pas un détail : un bouton ne se
 * replie jamais sur deux lignes (§5). `/admin/twilio-numbers` affichait
 * « Import » / « CSV » sur deux lignes — la faute est à la mise en page, jamais au
 * libellé, donc c'est le conteneur qui cède.
 */
const BASE =
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control ' +
  'font-semibold transition-colors duration-150 select-none ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background ' +
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none'

/**
 * `secondary` suit le §5 du design system — fond transparent, bordure
 * `border-secondary`, texte `foreground` — et non une teinte d'accent : deux
 * surfaces bleues côte à côte (primaire pleine + secondaire teintée) rendent
 * l'action principale illisible. Le survol pose une surface neutre, pas un accent.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-700 focus-visible:ring-primary-500',
  secondary:
    'bg-transparent text-foreground border border-border-secondary hover:bg-background-secondary ' +
    'active:bg-background-secondary focus-visible:ring-primary-500',
  ghost:
    'bg-transparent text-foreground-secondary hover:text-foreground hover:bg-background-secondary ' +
    'active:bg-background-secondary focus-visible:ring-primary-500',
  destructive:
    'bg-transparent text-danger-400 border border-danger-500 hover:bg-danger-500/10 ' +
    'active:bg-danger-500/10 focus-visible:ring-danger-500',
  destructiveSolid:
    'bg-danger-600 text-white hover:bg-danger-700 active:bg-danger-700 focus-visible:ring-danger-500',
}

/**
 * `md` : 42 px dessinés (`h-control`), cible 44 px assurée par `min-h-touch`.
 * `sm` : 32 px dessinés ; la cible est rendue par le pseudo-élément ci-dessous.
 */
const SIZES: Record<ButtonSize, string> = {
  md: 'h-control min-h-touch px-4 text-t4',
  sm: 'h-8 px-3 text-t5',
}

/**
 * Cible tactile de la taille `sm` : un pseudo-élément de 44 px de haut, centré,
 * étendu sur toute la largeur. Il n'occupe aucune place dans le flux — le bouton
 * reste dessiné à 32 px — et rend le doigt cliquable au-dessus et en dessous du
 * chrome visible. C'est exactement l'« autre façon » que le §3 prescrit face au
 * réflexe de grossir le contrôle.
 */
const SM_HIT_AREA =
  "before:absolute before:inset-x-0 before:top-1/2 before:h-touch before:-translate-y-1/2 before:content-['']"

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Icône à gauche du libellé. Remplacée par le spinner pendant `loading`. */
  icon?: LucideIcon
  /** Icône à droite du libellé (chevron, lien sortant…). */
  iconRight?: LucideIcon
  /** Désactive le bouton et substitue un spinner à l'icône de gauche. */
  loading?: boolean
  fullWidth?: boolean
  children?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    icon: Icon,
    iconRight: IconRight,
    loading = false,
    fullWidth = false,
    disabled,
    className = '',
    type = 'button',
    children,
    ...rest
  },
  ref
) {
  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'

  return (
    <button
      ref={ref}
      // `type` par défaut à "button" : un bouton dans un <form> qui n'est pas la
      // soumission ne doit pas soumettre. Le défaut HTML ("submit") a produit
      // assez de soumissions fantômes pour qu'on l'inverse ici.
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={[
        BASE,
        VARIANTS[variant],
        SIZES[size],
        size === 'sm' ? SM_HIT_AREA : '',
        fullWidth ? 'w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {loading ? (
        <Loader2 className={`${iconSize} animate-spin shrink-0`} aria-hidden="true" />
      ) : (
        Icon && <Icon className={`${iconSize} shrink-0`} aria-hidden="true" />
      )}
      {children}
      {IconRight && !loading && (
        <IconRight className={`${iconSize} shrink-0`} aria-hidden="true" />
      )}
    </button>
  )
})

export default Button
